import { useCallback, useEffect, useRef, useState } from 'react';
import { Modal } from '../../components/Modal';
import './objects.css';

type Game = 'menu' | 'snake' | '2048';

/** Spielautomat mit ein paar einfachen Einzelspieler-Klassikern (rein Client). */
export function ArcadeModal({ onClose }: { onClose: () => void }) {
  const [game, setGame] = useState<Game>('menu');
  return (
    <Modal title="Spielautomat" onClose={onClose}>
      {game === 'menu' && (
        <div className="arcade__menu">
          <button className="arcade__pick" onClick={() => setGame('snake')}>🐍 Snake</button>
          <button className="arcade__pick" onClick={() => setGame('2048')}>🔢 2048</button>
          <p className="arcade__hint">Steuerung: Pfeiltasten</p>
        </div>
      )}
      {game === 'snake' && <Snake onBack={() => setGame('menu')} />}
      {game === '2048' && <Game2048 onBack={() => setGame('menu')} />}
    </Modal>
  );
}

// --- Snake -----------------------------------------------------------------
const GRID = 16;
const CELL = 20;
const SPEED_MS = 120;

function Snake({ onBack }: { onBack: () => void }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [score, setScore] = useState(0);
  const [over, setOver] = useState(false);
  const [runId, setRunId] = useState(0); // Neustart

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx) return;
    let snake = [{ x: 8, y: 8 }];
    let dir = { x: 1, y: 0 };
    let pending = dir;
    let food = { x: 12, y: 8 };
    let dead = false;

    const key = (e: KeyboardEvent): void => {
      const map: Record<string, { x: number; y: number }> = {
        ArrowUp: { x: 0, y: -1 }, ArrowDown: { x: 0, y: 1 },
        ArrowLeft: { x: -1, y: 0 }, ArrowRight: { x: 1, y: 0 },
      };
      const nd = map[e.code];
      if (!nd) return;
      e.preventDefault();
      if (nd.x !== -dir.x || nd.y !== -dir.y) pending = nd; // keine 180-Grad-Wende
    };
    window.addEventListener('keydown', key);

    const draw = (): void => {
      ctx.fillStyle = '#1e2430';
      ctx.fillRect(0, 0, GRID * CELL, GRID * CELL);
      ctx.fillStyle = '#ff6b5e';
      ctx.fillRect(food.x * CELL + 2, food.y * CELL + 2, CELL - 4, CELL - 4);
      ctx.fillStyle = '#2ec4b6';
      for (const s of snake) ctx.fillRect(s.x * CELL + 1, s.y * CELL + 1, CELL - 2, CELL - 2);
    };

    const step = (): void => {
      if (dead) return;
      dir = pending;
      const head = { x: snake[0]!.x + dir.x, y: snake[0]!.y + dir.y };
      if (head.x < 0 || head.y < 0 || head.x >= GRID || head.y >= GRID || snake.some((s) => s.x === head.x && s.y === head.y)) {
        dead = true;
        setOver(true);
        return;
      }
      snake.unshift(head);
      if (head.x === food.x && head.y === food.y) {
        setScore((s) => s + 1);
        // neues Futter (deterministisch genug, nicht auf der Schlange)
        do {
          food = { x: Math.floor(pseudo() * GRID), y: Math.floor(pseudo() * GRID) };
        } while (snake.some((s) => s.x === food.x && s.y === food.y));
      } else {
        snake.pop();
      }
      draw();
    };

    let seed = 1234 + runId * 7;
    const pseudo = (): number => {
      seed = (seed * 1103515245 + 12345) & 0x7fffffff;
      return seed / 0x7fffffff;
    };

    draw();
    const timer = window.setInterval(step, SPEED_MS);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('keydown', key);
    };
  }, [runId]);

  const restart = (): void => {
    setScore(0);
    setOver(false);
    setRunId((r) => r + 1);
  };

  return (
    <div className="arcade__game">
      <div className="arcade__bar">
        <button onClick={onBack}>← Menü</button>
        <span>Punkte: {score}</span>
        {over && <button onClick={restart}>Neustart</button>}
      </div>
      <canvas ref={canvasRef} width={GRID * CELL} height={GRID * CELL} className="arcade__canvas" />
      {over && <p className="arcade__over">Game Over — {score} Punkte</p>}
    </div>
  );
}

// --- 2048 ------------------------------------------------------------------
type Board = number[][];

function empty2048(): Board {
  return Array.from({ length: 4 }, () => [0, 0, 0, 0]);
}
function spawn(board: Board): Board {
  const free: Array<[number, number]> = [];
  board.forEach((row, r) => row.forEach((v, c) => v === 0 && free.push([r, c])));
  if (free.length === 0) return board;
  const [r, c] = free[Math.floor(Math.random() * free.length)]!;
  const next = board.map((row) => [...row]);
  next[r]![c] = Math.random() < 0.9 ? 2 : 4;
  return next;
}
function slideRow(row: number[]): { row: number[]; gained: number } {
  const nums = row.filter((v) => v !== 0);
  let gained = 0;
  for (let i = 0; i < nums.length - 1; i += 1) {
    if (nums[i] === nums[i + 1]) {
      nums[i]! *= 2;
      gained += nums[i]!;
      nums.splice(i + 1, 1);
    }
  }
  while (nums.length < 4) nums.push(0);
  return { row: nums, gained };
}
function rotate(board: Board): Board {
  return board[0]!.map((_, c) => board.map((row) => row[c]!).reverse());
}
function move2048(board: Board, dir: 'left' | 'right' | 'up' | 'down'): { board: Board; gained: number; moved: boolean } {
  let b = board.map((row) => [...row]);
  const turns = { left: 0, up: 1, right: 2, down: 3 }[dir];
  for (let i = 0; i < turns; i += 1) b = rotate(b);
  let gained = 0;
  const slid = b.map((row) => {
    const r = slideRow(row);
    gained += r.gained;
    return r.row;
  });
  b = slid;
  for (let i = 0; i < (4 - turns) % 4; i += 1) b = rotate(b);
  const moved = JSON.stringify(b) !== JSON.stringify(board);
  return { board: b, gained, moved };
}

function Game2048({ onBack }: { onBack: () => void }) {
  const [board, setBoard] = useState<Board>(() => spawn(spawn(empty2048())));
  const [score, setScore] = useState(0);

  const doMove = useCallback((dir: 'left' | 'right' | 'up' | 'down') => {
    setBoard((prev) => {
      const { board: next, gained, moved } = move2048(prev, dir);
      if (!moved) return prev;
      setScore((s) => s + gained);
      return spawn(next);
    });
  }, []);

  useEffect(() => {
    const key = (e: KeyboardEvent): void => {
      const map: Record<string, 'left' | 'right' | 'up' | 'down'> = {
        ArrowLeft: 'left', ArrowRight: 'right', ArrowUp: 'up', ArrowDown: 'down',
      };
      const dir = map[e.code];
      if (!dir) return;
      e.preventDefault();
      doMove(dir);
    };
    window.addEventListener('keydown', key);
    return () => window.removeEventListener('keydown', key);
  }, [doMove]);

  const restart = (): void => {
    setBoard(spawn(spawn(empty2048())));
    setScore(0);
  };

  return (
    <div className="arcade__game">
      <div className="arcade__bar">
        <button onClick={onBack}>← Menü</button>
        <span>Punkte: {score}</span>
        <button onClick={restart}>Neu</button>
      </div>
      <div className="g2048">
        {board.flat().map((v, i) => (
          <div key={i} className={`g2048__cell g2048__cell--${v}`}>{v || ''}</div>
        ))}
      </div>
    </div>
  );
}
